import React from 'react';
import { useTranslation } from 'react-i18next';

import Loader from '../Loader';
import NotificationCount from '../NotificationCount';
import {
  FoldoutBtn,
  FoldoutBtnContent,
  FoldoutBtnIcon,
  FoldoutDialog,
  FoldoutItem,
  FoldoutList,
  FoldoutUiBlock,
  Foldout as StyledFoldout,
  Wrapper,
} from './wrapper';

export interface IFoldoutItem {
  displayName: React.ReactNode;
  title: string;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  tone?: 'link' | 'error';
  disabled?: boolean;
  isLoading?: boolean;
  icon?: React.ReactNode;
  notificationCount?: number;
}

export interface IFoldout {
  children: React.ReactNode;
  items: IFoldoutItem[][];
  isOpen?: boolean;
  onClose: (e: React.MouseEvent<HTMLDivElement>) => void;
  posX?: 'start' | 'end';
  posXSm?: 'start' | 'end';
  posXMd?: 'start' | 'end';
  posY?: 'top' | 'bottom';
  posYSm?: 'top' | 'bottom';
  posYMd?: 'top' | 'bottom';
}

const Foldout = ({
  children,
  items,
  isOpen,
  onClose,
  posX,
  posXSm,
  posXMd,
  posY,
  posYSm,
  posYMd,
}: IFoldout) => {
  const { t } = useTranslation(['common']);

  return (
    <Wrapper>
      {children}
      {isOpen && (
        <StyledFoldout
          $posX={posX}
          $posXSm={posXSm}
          $posXMd={posXMd}
          $posY={posY}
          $posYSm={posYSm}
          $posYMd={posYMd}
        >
          <FoldoutUiBlock
            onClick={onClose}
            aria-label={t('close', { ns: 'common' })}
            aria-hidden="true"
          />
          <FoldoutDialog>
            {items.map((group, i) => (
              <FoldoutList key={`foldout-group-${i}`}>
                {group.map((item, j) => (
                  <FoldoutItem key={`${item.displayName}-${i}-${j}`}>
                    <FoldoutBtn
                      title={item.title}
                      onClick={item.onClick}
                      disabled={item.disabled}
                      $tone={item.tone}
                    >
                      {item.icon && <FoldoutBtnIcon>{item.icon}</FoldoutBtnIcon>}
                      <FoldoutBtnContent>{item.displayName}</FoldoutBtnContent>
                      {item.notificationCount && (
                        <FoldoutBtnContent>
                          <NotificationCount>{item.notificationCount}</NotificationCount>
                        </FoldoutBtnContent>
                      )}
                      {item.isLoading && (
                        <FoldoutBtnContent>
                          <Loader inlineBlock />
                        </FoldoutBtnContent>
                      )}
                    </FoldoutBtn>
                  </FoldoutItem>
                ))}
              </FoldoutList>
            ))}
          </FoldoutDialog>
        </StyledFoldout>
      )}
    </Wrapper>
  );
};

export default Foldout;
